import { SupabaseClient } from '@supabase/supabase-js';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { addHours } from 'date-fns';
import { supabase as supabaseClient } from '../client/browser-cookie';
import { userService, UserProfileData } from './user';
import { healthRecordService } from './health-record';
import { episodeService, isGroupTherapyType, type TherapyType } from './episode';
import { consentService } from './consent';
import { validatePromoCode } from './promo';
import { updateWalletTransactionStatus, findBestAvailableWallet, reserveWalletSession, mapWalletSourceToPaymentSource } from './wallet';
import { v4 as uuidv4 } from 'uuid';

export interface TimeSlot {
  start: string; // ISO string in client timezone
  end: string; // ISO string in client timezone
  available: boolean;
}

export interface AvailabilityBlock {
  id: string;
  start: string;
  end: string;
  sessions: Array<{
    id: string;
    start: string;
    end: string;
    status: string;
  }>;
}

export interface AvailabilityResponse {
  slots: TimeSlot[];
  timezone: string;
}

/**
 * Get therapist availability for booking
 * @param proId - Therapist ID
 * @param clientTimezone - Client's timezone (e.g., 'America/New_York')
 * @param startDate - Optional start date for availability window (UTC string)
 * @param endDate - Optional end date for availability window (UTC string)
 */
export async function getProAvailability(
  proId: string,
  clientTimezone: string,
  startDate?: string,
  endDate?: string,
  supabase?: any
): Promise<AvailabilityResponse> {
  const client = (supabase || supabaseClient) as any;

  // Default to 24 hours from now as start, and 30 days from start as end
  const now = new Date();
  const twentyFourHoursFromNow = addHours(now, 24);
  const queryStart = startDate || twentyFourHoursFromNow.toISOString();
  const queryEnd = endDate || addHours(twentyFourHoursFromNow, 30 * 24).toISOString();

  // Format dates for database comparison - remove 'Z' suffix to match storage format
  // Database stores timestamps without timezone suffix
  const formatForDbQuery = (isoString: string) => {
    // Convert ISO string to UTC datetime without 'Z' suffix
    // e.g., "2024-04-01T07:00:00.000Z" -> "2024-04-01 07:00:00"
    return isoString.replace('T', ' ').replace('Z', '').replace(/\.\d{3}$/, '');
  };

  const queryStartDb = formatForDbQuery(queryStart);
  const queryEndDb = formatForDbQuery(queryEnd);

  // Fetch availability, blocked time, and existing sessions for the therapist
  const [availabilityResult, blockedResult, sessionsResult] = await Promise.all([
    // Fetch availability blocks (isBlock = false)
    client
      .from('availability')
      .select(`
        id,
        start,
        end
      `)
      .eq('proId', proId)
      .eq('isBlock', false)
      .gte('start', queryStartDb)
      .lte('start', queryEndDb)
      .order('start'),

    // Fetch blocked time (isBlock = true)
    client
      .from('availability')
      .select(`
        id,
        start,
        end
      `)
      .eq('proId', proId)
      .eq('isBlock', true)
      .gte('start', queryStartDb)
      .lte('start', queryEndDb),

    // Fetch therapist's sessions in the time range
    client
      .from('sessions')
      .select('id, start, end, status')
      .eq('therapistId', proId)
      .in('status', ['SCHEDULED', 'CONFIRMED', 'COMPLETED'])
      .gte('start', queryStartDb)
      .lte('start', queryEndDb)
  ]);

  if (availabilityResult.error) {
    console.error('Error fetching availability:', availabilityResult.error);
    throw availabilityResult.error;
  }

  if (blockedResult.error) {
    console.error('Error fetching blocked time:', blockedResult.error);
    throw blockedResult.error;
  }

  if (sessionsResult.error) {
    console.error('Error fetching sessions:', sessionsResult.error);
    throw sessionsResult.error;
  }

  const availabilities = availabilityResult.data;
  const blockedTimes = blockedResult.data || [];
  const existingSessions = sessionsResult.data || [];
  
  // Debug blocked times
  if (blockedTimes.length > 0) {
    console.log('Blocked times:', blockedTimes.slice(0, 3));
  }


  // Generate 1-hour time slots from availability blocks
  const slots: TimeSlot[] = [];

  if (availabilities) {
    for (const availability of availabilities) {
      // Database strings are in UTC but without 'Z' suffix
      // Append 'Z' to ensure they're treated as UTC
      const blockStartUTC = availability.start + 'Z';
      const blockEndUTC = availability.end + 'Z';
      
      // Filter sessions that overlap with this availability block
      // Include any session that starts before the block ends AND ends after the block starts
      const activeSessions = existingSessions
        .filter((session: any) => {
          const sessionStart = new Date(session.start + 'Z');
          const sessionEnd = new Date(session.end + 'Z');
          const blockStart = new Date(blockStartUTC);
          const blockEnd = new Date(blockEndUTC);
          // Check for any overlap with the availability block
          return sessionStart < blockEnd && sessionEnd > blockStart;
        })
        .sort((a: any, b: any) => a.start.localeCompare(b.start));
      
      // Parse as UTC by using the Z suffix
      let currentDateUTC = new Date(blockStartUTC);
      const endDateUTC = new Date(blockEndUTC);
      
      // Check if the start of this availability block is blocked
      const startBlocker = blockedTimes.find((blocked: any) => {
        const blockedStartUTC = new Date(blocked.start + 'Z');
        const blockedEndUTC = new Date(blocked.end + 'Z');
        return blockedStartUTC <= currentDateUTC && blockedEndUTC > currentDateUTC;
      });
      
      if (startBlocker) {
        // Start from the end of the blocker instead
        currentDateUTC = new Date(startBlocker.end + 'Z');
        console.log('Availability starts blocked, jumping to:', currentDateUTC.toISOString());
      }
      
      // Generate hourly slots
      let iterations = 0;
      const maxIterations = 1000; // Safety check to prevent infinite loops
      
      while (currentDateUTC < endDateUTC && iterations < maxIterations) {
        iterations++;
        
        // Ensure we're making progress
        const startOfLoop = currentDateUTC.getTime();
        
        const nextDateUTC = addHours(currentDateUTC, 1);
        
        // Only process if we have at least 1 hour available
        if (nextDateUTC <= endDateUTC) {
          const slotStartUTC = currentDateUTC.toISOString();
          const slotEndUTC = nextDateUTC.toISOString();
          
          // Check for interfering sessions or blocks
          const interferingSession = activeSessions.find((session: any) => {
            // Parse UTC times from database (append 'Z' to ensure UTC parsing)
            const sessionStartTime = new Date(session.start + 'Z').getTime();
            const sessionEndTime = new Date(session.end + 'Z').getTime();
            // slotStartUTC and slotEndUTC are already ISO strings with Z
            const slotStartTime = new Date(slotStartUTC).getTime();
            const slotEndTime = new Date(slotEndUTC).getTime();
            
            const overlaps = slotStartTime < sessionEndTime && slotEndTime > sessionStartTime;
            
            if (overlaps) {
              console.log('Session interference detected:', {
                slotStart: slotStartUTC,
                slotEnd: slotEndUTC,
                sessionStart: session.start + 'Z',
                sessionEnd: session.end + 'Z'
              });
            }
            
            return overlaps;
          });
          
          const interferingBlock = blockedTimes.find((blocked: any) => {
            const blockedStartUTC = blocked.start + 'Z';
            const blockedEndUTC = blocked.end + 'Z';
            // Check for actual overlap, not just touching boundaries
            const blockStart = new Date(blockedStartUTC).getTime();
            const blockEnd = new Date(blockedEndUTC).getTime();
            const slotStart = new Date(slotStartUTC).getTime();
            const slotEnd = new Date(slotEndUTC).getTime();
            return slotStart < blockEnd && slotEnd > blockStart;
          });
          
          if (interferingSession || interferingBlock) {
            // Find the latest end time to skip to
            let skipToTime = nextDateUTC; // Default to skipping the current slot
            
            if (interferingSession) {
              const sessionEnd = new Date(interferingSession.end + 'Z');
              if (sessionEnd > skipToTime) skipToTime = sessionEnd;
              console.log('Skipping to end of session:', {
                sessionEnd: sessionEnd.toISOString(),
                availabilityEnd: endDateUTC.toISOString(),
                hasMoreTime: sessionEnd < endDateUTC
              });
            }
            
            if (interferingBlock) {
              const blockEnd = new Date(interferingBlock.end + 'Z');
              if (blockEnd > skipToTime) skipToTime = blockEnd;
              console.log('Block interference:', {
                slotStart: slotStartUTC,
                slotEnd: slotEndUTC,
                blockStart: interferingBlock.start,
                blockEnd: interferingBlock.end,
                skipTo: blockEnd.toISOString()
              });
            }
            
            currentDateUTC = skipToTime;
          } else {
            // No interference, check if slot is at least 24 hours from now
            const twentyFourHoursFromNow = addHours(new Date(), 24);
            const slotStartDate = new Date(slotStartUTC);

            // Only add slot if it's at least 24 hours in the future
            if (slotStartDate >= twentyFourHoursFromNow) {
              const slotStartClient = formatInTimeZone(slotStartUTC, clientTimezone, "yyyy-MM-dd'T'HH:mm:ssXXX");
              const slotEndClient = formatInTimeZone(slotEndUTC, clientTimezone, "yyyy-MM-dd'T'HH:mm:ssXXX");

              slots.push({
                start: slotStartClient,
                end: slotEndClient,
                available: true
              });
            }

            currentDateUTC = nextDateUTC;
          }
        } else {
          // Can't fit another full slot, move to end
          currentDateUTC = endDateUTC;
        }
        
        // Safety check: ensure we're making progress
        if (currentDateUTC.getTime() === startOfLoop) {
          console.error('Infinite loop detected in slot generation');
          break;
        }
      }
      
      if (iterations >= maxIterations) {
        console.error('Max iterations reached in slot generation');
      }
    }
  }

  return {
    slots,
    timezone: clientTimezone
  };
}

export interface Session {
  id: string;
  sessionType: 'INTAKE' | 'FOLLOWUP' | 'CONSULTATION';
  episodeId?: string; // Optional for group sessions
  groupId?: string; // For couples/group sessions
  therapistId: string;
  start: Date | string;
  end: Date | string;
  timezone: string;
  status: 'SCHEDULED' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW' | 'LATE_CANCEL';
  cancellationReason?: string;
  cancelledAt?: Date | string;
  cancelledBy?: string;
  rate: number;
  duration: number;
  paymentStatus: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED' | 'WAIVED';
  paymentDate?: Date | string;
  paymentReference?: string;
  isPartnershipCovered: boolean;
  partnershipId?: string;
  // Wallet/MHAP coverage
  paymentSource?: 'SELF_PAY' | 'EMPLOYER_MHAP' | 'PROMO' | 'PARTNERSHIP' | 'INTERNAL';
  walletId?: string;
  employerOrgId?: string;
  memberId?: string;
  // Attendance tracking
  expectedAttendees?: string[]; // User IDs
  actualAttendees?: string[]; // User IDs
  // Group data for couples/group therapy
  group?: {
    id: string;
    name: string;
    groupType: 'COUPLES' | 'FAMILY' | 'GROUP';
  };
  // Expected attendees with user details (when fetched with join)
  attendees?: Array<{
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    email?: string;
    phone?: string;
    emergencyName?: string;
    emergencyPhone?: string;
  }>;
  createdAt: Date | string;
  updatedAt: Date | string;
  // Optional relations that might be included
  episode?: {
    id: string;
    healthRecordId: string;
    therapyType?: string;
    userId: string;
    user?: {
      firstName: string;
      lastName: string;
      preferredName?: string;
      email: string;
      phone?: string;
    };
  };
  progressNote?: {
    id: string;
    status: 'DRAFT' | 'SUBMITTED';
  };
  // Employer organization (when fetched with join)
  employerOrg?: {
    id: string;
    name: string;
  };
  // Wallet info (when fetched with join)
  wallet?: {
    id: string;
    totalSessions: number;
    sourceType: 'EMPLOYER_MHAP' | 'PROMO' | 'GIFT' | 'PARTNERSHIP' | 'INTERNAL_OVERRIDE';
    validFrom: Date | string;
    validUntil?: Date | string;
    name?: string;
  };
}

/**
 * Get therapist's sessions for a date range
 * @param therapistId - The therapist's ID
 * @param startDate - Start of the date range (ISO string)
 * @param endDate - End of the date range (ISO string)
 * @param supabase - Optional Supabase client
 */
export async function getTherapistSessions(
  therapistId: string,
  startDate: string,
  endDate: string,
  supabase?: SupabaseClient
): Promise<Session[]> {
  const client = (supabase || supabaseClient) as any;

  const { data: sessionsData, error } = await (client as any)
    .from('sessions')
    .select(`
      *,
      episode:episodes!episodeId(
        id,
        healthRecordId,
        therapyType,
        userId,
        user:users!userId(
          firstName,
          lastName,
          preferredName,
          email,
          phone,
          emergencyName,
          emergencyPhone,
          unit,
          street,
          city,
          province,
          postalCode
        )
      ),
      group:therapy_groups!groupId(
        id,
        name,
        groupType
      ),
      employerOrg:employer_organizations!employerOrgId(
        id,
        name
      ),
      wallet:session_wallets!walletId(
        id,
        totalSessions,
        sourceType,
        validFrom,
        validUntil,
        name
      )
    `)
    .eq('therapistId', therapistId)
    .gte('start', startDate)
    .lte('end', endDate)
    .in('status', ['SCHEDULED', 'CONFIRMED', 'COMPLETED', 'NO_SHOW', 'LATE_CANCEL'])
    .order('start', { ascending: true });

  if (error) {
    console.error('Error fetching therapist sessions:', error);
    throw error;
  }

  if (!sessionsData || sessionsData.length === 0) {
    return [];
  }

  // Data includes episode information via the join
  // Note: Progress notes are NOT fetched here for performance
  // They will be fetched when clicking on a specific booking
  return sessionsData;
}

/**
 * Get attendees for a session. For group/couples therapy, returns all group members.
 * For individual therapy, returns the expected attendees.
 * @param sessionId - The session ID
 * @param supabase - Optional Supabase client
 */
export async function getSessionAttendees(
  sessionId: string,
  supabase?: SupabaseClient
) {
  const client = (supabase || supabaseClient) as any;

  // First get the session with groupId and expectedAttendees
  const { data: session, error: sessionError } = await client
    .from('sessions')
    .select('groupId, expectedAttendees, episodeId')
    .eq('id', sessionId)
    .single();

  if (sessionError || !session) {
    console.error('Error fetching session:', sessionError);
    return [];
  }

  // If this is a group/couples session, get all members from the therapy group
  if (session.groupId) {
    const { data: therapyGroup, error: groupError } = await client
      .from('therapy_groups')
      .select(`
        episodes (
          userId,
          user:users!episodes_userId_fkey (
            id,
            firstName,
            lastName,
            preferredName,
            email,
            phone,
            emergencyName,
            emergencyPhone,
            unit,
            street,
            city,
            province,
            postalCode
          )
        )
      `)
      .eq('id', session.groupId)
      .single();

    if (groupError || !therapyGroup) {
      console.error('Error fetching therapy group:', groupError);
      return [];
    }

    // Extract all users from the group's episodes
    const attendees = therapyGroup.episodes
      ?.map((episode: any) => episode.user)
      .filter((user: any) => user !== null) || [];

    return attendees;
  }

  // For individual therapy, fall back to expectedAttendees
  if (!session.expectedAttendees || session.expectedAttendees.length === 0) {
    // If no expectedAttendees, try to get the user from the episode
    if (session.episodeId) {
      const { data: episode, error: episodeError } = await client
        .from('episodes')
        .select(`
          userId,
          user:users!episodes_userId_fkey (
            id,
            firstName,
            lastName,
            preferredName,
            email,
            phone,
            emergencyName,
            emergencyPhone,
            unit,
            street,
            city,
            province,
            postalCode
          )
        `)
        .eq('id', session.episodeId)
        .single();

      if (episodeError || !episode || !episode.user) {
        console.error('Error fetching episode user:', episodeError);
        return [];
      }

      return [episode.user];
    }

    return [];
  }

  // Fetch user details for all expected attendees
  const { data: attendees, error: usersError } = await client
    .from('users')
    .select('id, firstName, lastName, preferredName, email, phone, emergencyName, emergencyPhone')
    .in('id', session.expectedAttendees);

  if (usersError) {
    console.error('Error fetching attendees:', usersError);
    return [];
  }

  return attendees || [];
}

export interface CreateInitialSessionParams {
  userId: string; // User ID from the users table
  therapistId: string;
  start: string;
  end: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientPhone: string;
  notes?: string;
  timezone: string;
  // Additional fields for comprehensive session creation
  therapyType?: TherapyType;
  sessionRate?: number;
  tier?: string;
  matchingQuizId?: string;
  partnershipId?: string;
  partnershipSessions?: number;
  accessCode?: string;
  promoCode?: string;
  groupId?: string; // For couples/group therapy sessions
  // For updating user profile
  preferredName?: string;
  gender?: string;
  pronoun?: string;
  birthday?: string;
  unit?: string;
  street?: string;
  city?: string;
  province?: string;
  country?: string;
  postalCode?: string;
  emergencyName?: string;
  emergencyPhone?: string;
  referrer?: string;
  referredBy?: string;
  referralCode?: string;
  marketingOptIn?: boolean;
  smsOptIn?: boolean;
  newsletterOptIn?: boolean;
  // Consent fields for initial booking
  termsAccepted?: boolean;
  privacyAccepted?: boolean;
  consentAccepted?: boolean;
  cancellationAccepted?: boolean;
  crisisAccepted?: boolean;
  fileSharingAccepted?: boolean;
  // IP address for consent tracking
  ipAddress?: string;
  // Allow booking even if there's an existing episode with a different therapist
  allowBooking?: boolean;
}

/**
 * Create a therapy session
 */
async function createTherapySession(
  episodeId: string,
  healthRecordId: string, // Kept for potential future use (e.g., validation)
  params: CreateInitialSessionParams,
  client: SupabaseClient
): Promise<string> {
  const isPartnershipCovered = !!params.partnershipId;
  // Use promoCode that was verified from database
  const verifiedPromoCode = (params as any).promoCode || params.promoCode;
  let isFreeSession = !!verifiedPromoCode;
  const sessionRate = (params as any).sessionRate || params.sessionRate || 0;
  let actualRate = sessionRate;

  // For partnership sessions, the rate is already set correctly in buildBookingParams
  // and the payment status should be PENDING (to be paid by partnership)
  
  // Check for free sessions based on therapy type
  const sessionGroupId = (params as any).groupId;

  if (sessionGroupId) {
    // For couples/group therapy, check the therapy group's free sessions
    const { data: group, error: groupError } = await client
      .from('therapy_groups')
      .select('freeSessionsAllocated, sessionRate')
      .eq('id', sessionGroupId)
      .single();

    if (!groupError && group) {
      // Count already used free sessions for this group using the updated function
      const freeSessionsUsed = await episodeService.getFreeSessionsUsed(sessionGroupId, client, 'group');
      const hasUnusedFreeSessions = group.freeSessionsAllocated > freeSessionsUsed;

      if (hasUnusedFreeSessions) {
        console.log('[Session] Using free session from therapy group allocation');
        isFreeSession = true;
        actualRate = 0;
      } else {
        // Use the group's session rate
        actualRate = group.sessionRate || sessionRate;
      }
    }
  } else {
    // For individual therapy, check the episode's free sessions
    const { data: episode, error: episodeError } = await client
      .from('episodes')
      .select('freeSessionsAllocated, sessionRate')
      .eq('id', episodeId)
      .single();

    if (!episodeError && episode) {
      // Count already used free sessions using the existing function
      const freeSessionsUsed = await episodeService.getFreeSessionsUsed(episodeId, client, 'episode');
      const hasUnusedFreeSessions = episode.freeSessionsAllocated > freeSessionsUsed;

      if (hasUnusedFreeSessions) {
        console.log('[Session] Using free session from episode allocation');
        isFreeSession = true;
        actualRate = 0;
      } else if (isFreeSession) {
        // If flagged as free but no sessions available, still honor it for first session
        actualRate = 0;
      } else {
        // Use the episode's session rate (which may have promo discount applied)
        actualRate = episode.sessionRate || sessionRate;
      }
    } else if (isFreeSession) {
      actualRate = 0;
    }
  }
  
  // Convert start and end times to UTC
  // The times come in as ISO strings with timezone offset already included
  // Just parse them directly - JavaScript Date will handle the timezone offset
  const startUtc = new Date(params.start);
  const endUtc = new Date(params.end);
  
  // Only set attendance tracking for group/couples/family therapy.
  // Explicit list beats "!= INDIVIDUAL" — otherwise the ICBT enum
  // values (ICBT_UNSPIRAL, ICBT_UPLIFT), which run as individual
  // sessions, would get mis-classified as group sessions.
  const therapyType = (params as any).therapyType || params.therapyType;
  console.log('[createTherapySession] Therapy type:', therapyType, 'groupId:', sessionGroupId);
  const isGroupTherapy = Boolean(
    sessionGroupId || isGroupTherapyType(therapyType?.toUpperCase?.() ?? therapyType),
  );

  console.log('[createTherapySession] Session creation params:', {
    episodeId,
    sessionGroupId,
    isGroupTherapy,
    willSetEpisodeId: !isGroupTherapy ? episodeId : null,
    willSetGroupId: isGroupTherapy ? sessionGroupId : null
  });

  if (!episodeId && !sessionGroupId) {
    console.error('[createTherapySession] WARNING: Creating session without episodeId or groupId!');
    throw new Error('We cannot create a session without episodeId or groupId!');
  }

  const { data: newSession, error } = await client
    .from('sessions')
    .insert({
      id: uuidv4(),
      sessionType: 'INTAKE',
      episodeId: isGroupTherapy ? null : episodeId, // Only include episodeId for individual therapy
      groupId: isGroupTherapy ? sessionGroupId : null, // Only include groupId for couples/group sessions
      therapistId: params.therapistId,
      start: startUtc,
      end: endUtc,
      timezone: params.timezone,
      expectedAttendees: isGroupTherapy ? [params.userId] : [],
      actualAttendees: [],
      status: 'SCHEDULED',
      rate: actualRate,
      duration: 50, // 50 minute sessions
      paymentStatus: isFreeSession ? 'WAIVED' : (isPartnershipCovered ? 'PENDING' : 'PENDING'),
      isPartnershipCovered: isPartnershipCovered,
      partnershipId: params.partnershipId,
      createdAt: new Date(),
      updatedAt: new Date()
    })
    .select('id')
    .single();
  
  if (error) throw error;
  return newSession.id;
}

/**
 * Update session status with metadata
 * @param sessionId - The session ID to update
 * @param status - The new status
 * @param metadata - Additional metadata for the status change
 * @param supabase - Optional supabase client
 */
export async function updateSessionStatus(
  sessionId: string,
  status: 'SCHEDULED' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'LATE_CANCEL' | 'NO_SHOW',
  metadata?: {
    cancelledBy?: 'THERAPIST' | 'CLIENT' | 'SYSTEM';
    cancellationReason?: string;
    paymentStatus?: 'PENDING' | 'PAID' | 'FAILED' | 'CANCELLED' | 'UNPAID' | 'WAIVED';
    emergencyWaiver?: boolean;
    actualAttendees?: string[];
    paidByUserId?: string;
  },
  supabase?: SupabaseClient
): Promise<void> {
  const client = (supabase || supabaseClient) as any;
  
  const updateData: any = {
    status,
    updatedAt: new Date().toISOString()
  };
  
  // Add payment status from metadata if provided
  if (metadata?.paymentStatus) {
    updateData.paymentStatus = metadata.paymentStatus;
  }

  // Add paidByUserId if provided (tracks who paid for the session)
  if (metadata?.paidByUserId) {
    updateData.paidByUserId = metadata.paidByUserId;
    console.log('[updateSessionStatus] Setting paidByUserId:', {
      sessionId,
      paidByUserId: metadata.paidByUserId
    });
  }

  // Add actual attendees when marking as completed (for group/couples therapy)
  if (status === 'COMPLETED' && metadata?.actualAttendees) {
    updateData.actualAttendees = metadata.actualAttendees;
    console.log('[updateSessionStatus] Setting actual attendees:', {
      sessionId,
      actualAttendees: metadata.actualAttendees
    });
  }

  // Add cancellation metadata if cancelling (either regular or late cancel)
  if ((status === 'CANCELLED' || status === 'LATE_CANCEL') && metadata) {
    updateData.cancelledBy = metadata.cancelledBy || 'THERAPIST';
    updateData.cancellationReason = metadata.cancellationReason;
    updateData.cancelledAt = new Date().toISOString();
    
    // For regular cancellations (not late), also update payment status to CANCELLED if not already set
    // For late cancellations, keep payment status as-is unless explicitly provided
    if (status === 'CANCELLED' && !metadata.paymentStatus) {
      updateData.paymentStatus = 'CANCELLED';
    }
  }
  
  // Add no-show metadata
  if (status === 'NO_SHOW' && metadata) {
    updateData.cancelledBy = metadata.cancelledBy || 'CLIENT';
    updateData.cancellationReason = metadata.cancellationReason; // This contains the no-show notes
    updateData.cancelledAt = new Date().toISOString();
    
    console.log('[updateSessionStatus] NO_SHOW update data:', {
      sessionId,
      updateData,
      metadata
    });
  }
  
  console.log('[updateSessionStatus] Final update data:', {
    sessionId,
    status,
    updateData
  });
  
  console.log('[updateSessionStatus] About to call Supabase update...');
  
  // Skip auth check for now - it seems to be causing the hang
  // Just try the update directly and let Supabase handle auth
  console.log('[updateSessionStatus] Skipping auth check, proceeding with update...');
  
  const { error } = await client
    .from('sessions')
    .update(updateData)
    .eq('id', sessionId);

  console.log('[updateSessionStatus] Supabase update completed, error:', error);

  if (error) {
    console.error('[updateSessionStatus] Error updating session:', error);
    throw error;
  }

  console.log('[updateSessionStatus] Session updated successfully:', sessionId, 'with status:', status);

  // Update wallet transaction status based on session outcome
  if (status === 'COMPLETED' || status === 'NO_SHOW' || status === 'LATE_CANCEL' || status === 'CANCELLED') {
    try {
      // RELEASED = session cancelled (returns to pool), CONSUMED = session used
      // Release if: cancelled, emergency waiver, OR therapist-initiated late cancel/no-show
      const isTherapistInitiated = updateData.cancelledBy === 'THERAPIST';
      const walletStatus = (
        status === 'CANCELLED' ||
        updateData.paymentStatus === 'CANCELLED' ||
        ((status === 'LATE_CANCEL' || status === 'NO_SHOW') && isTherapistInitiated)
      )
        ? 'RELEASED'
        : 'CONSUMED';

      const reason = status === 'COMPLETED'
        ? 'Session completed'
        : status === 'CANCELLED'
          ? 'Session cancelled'
          : status === 'NO_SHOW'
            ? (isTherapistInitiated ? 'No-show - therapist initiated' : updateData.paymentStatus === 'CANCELLED' ? 'No-show - emergency waiver' : 'No-show')
            : (isTherapistInitiated ? 'Late cancel - therapist initiated' : updateData.paymentStatus === 'CANCELLED' ? 'Late cancel - emergency waiver' : 'Late cancellation');

      const updated = await updateWalletTransactionStatus(sessionId, walletStatus, reason, client);
      if (updated) {
        console.log('[updateSessionStatus] Wallet transaction updated:', { sessionId, walletStatus, reason });
      }
    } catch (walletError) {
      console.error('[updateSessionStatus] Error updating wallet transaction:', walletError);
      // Non-fatal - session status is already updated
    }
  }

  // If marking a FOLLOWUP session as COMPLETED, automatically update episode status to ACTIVE
  if (status === 'COMPLETED') {
    console.log('[updateSessionStatus] ===== STARTING EPISODE STATUS UPDATE CHECK =====');
    console.log('[updateSessionStatus] Session marked as COMPLETED, checking if episode needs update...');

    try {
      // Get session details to check session type and episode/group info
      console.log('[updateSessionStatus] Fetching session details for:', sessionId);

      const { data: session, error: sessionError } = await client
        .from('sessions')
        .select('id, sessionType, episodeId, groupId')
        .eq('id', sessionId)
        .single();

      console.log('[updateSessionStatus] Session query result:', {
        success: !sessionError,
        error: sessionError?.message,
        data: session
      });

      if (sessionError || !session) {
        console.error('[updateSessionStatus] ❌ Could not fetch session for episode update:', sessionError);
        return; // Don't fail the whole operation if episode update fails
      }

      console.log('[updateSessionStatus] ✅ Session data retrieved successfully');
      console.log('[updateSessionStatus] Checking if episode status needs update:', {
        sessionId,
        sessionType: session.sessionType,
        episodeId: session.episodeId,
        groupId: session.groupId
      });

      // Only update episode status to ACTIVE if this is a FOLLOWUP session
      if (session.sessionType === 'FOLLOWUP') {
        console.log('[updateSessionStatus] ✅ Session is FOLLOWUP type - proceeding with episode update');

        // For couples/group therapy with groupId, update all episodes in that group
        if (session.groupId) {
          console.log('[updateSessionStatus] 🔄 COUPLES/GROUP THERAPY detected');
          console.log('[updateSessionStatus] Updating all episodes with groupId:', session.groupId);

          const { error: groupUpdateError, count } = await client
            .from('episodes')
            .update({
              status: 'ACTIVE',
              updatedAt: new Date().toISOString()
            })
            .eq('groupId', session.groupId)
            .eq('status', 'INTAKE'); // Only update if still at INTAKE

          if (groupUpdateError) {
            console.error('[updateSessionStatus] ❌ Error updating group episodes:', groupUpdateError);
          } else {
            console.log('[updateSessionStatus] ✅ Successfully updated episodes with groupId to ACTIVE:', session.groupId);
            console.log('[updateSessionStatus] Number of episodes updated:', count);
          }
        }
        // For individual/family therapy with episodeId, update single episode
        else if (session.episodeId) {
          console.log('[updateSessionStatus] 🔄 INDIVIDUAL THERAPY detected');
          console.log('[updateSessionStatus] Updating episode:', session.episodeId);

          const { error: episodeUpdateError, count } = await client
            .from('episodes')
            .update({
              status: 'ACTIVE',
              updatedAt: new Date().toISOString()
            })
            .eq('id', session.episodeId)
            .eq('status', 'INTAKE'); // Only update if still at INTAKE

          if (episodeUpdateError) {
            console.error('[updateSessionStatus] ❌ Error updating episode status:', episodeUpdateError);
          } else {
            console.log('[updateSessionStatus] ✅ Successfully updated episode to ACTIVE:', session.episodeId);
            console.log('[updateSessionStatus] Number of episodes updated:', count);
          }
        } else {
          console.warn('[updateSessionStatus] ⚠️ WARNING: No episodeId or groupId found - cannot update episode status');
        }
      } else {
        console.log('[updateSessionStatus] ℹ️ Not a FOLLOWUP session (type: ' + session.sessionType + ') - no episode status update needed');
      }
    } catch (error) {
      console.error('[updateSessionStatus] ❌ ERROR in episode status update logic:', error);
      console.error('[updateSessionStatus] Error details:', JSON.stringify(error, null, 2));
      // Don't throw - we don't want to fail the session update if episode update fails
    }

    console.log('[updateSessionStatus] ===== EPISODE STATUS UPDATE CHECK COMPLETE =====');
  }
}

/**
 * Create an initial therapy session with all necessary records
 */
export async function createInitialSession(
  params: CreateInitialSessionParams,
  supabase?: SupabaseClient
): Promise<{
  sessionId: string;
  episodeId: string;
  healthRecordId: string;
  userId: string;
  groupId?: string; // For couples/group therapy
  emailData?: any; // Data needed for sending confirmation email
}> {
  const client = (supabase || supabaseClient) as any;
  
  console.log('[createInitialSession] Starting with params:', {
    therapyType: params.therapyType,
    userId: params.userId,
    therapistId: params.therapistId,
    matchingQuizId: params.matchingQuizId,
    sessionRate: params.sessionRate,
    tier: params.tier,
    promoCode: params.promoCode
  });

  try {
    // 0. If we have a matchingQuizId, fetch critical data from the database
    // This is more secure than trusting client-provided values
    let effectivePromoCode = params.promoCode;
    let effectiveRate = params.sessionRate;
    let effectiveTherapyType = params.therapyType;
    let effectiveTherapistTier = params.tier;
    
    if (params.matchingQuizId) {
      console.log('[createInitialSession] Fetching secure quiz data:', params.matchingQuizId);
      const { data: quizData, error: quizError } = await client
        .from('matching_quizzes')
        .select('promoCode, rate, type, tier')
        .eq('id', params.matchingQuizId)
        .single();
      
      if (quizData && !quizError) {
        // Override client-provided values with secure database values
        console.log('[createInitialSession] Using secure quiz data:', {
          promoCode: quizData.promoCode,
          rate: quizData.rate,
          type: quizData.type,
          tier: quizData.tier
        });
        
        // Prioritize user-entered promo code over quiz promo code
        effectivePromoCode = effectivePromoCode || quizData.promoCode;
        effectiveRate =  effectiveRate || quizData.rate;
        // Convert therapy type to uppercase for enum compatibility
        effectiveTherapyType = effectiveTherapyType || quizData.type?.toUpperCase();
        effectiveTherapistTier = effectiveTherapistTier || quizData.tier;
      }
    }
    
    // 1. Update user profile with any new information
    const userProfileData: UserProfileData = {
      id: params.userId,
      firstName: params.clientFirstName,
      lastName: params.clientLastName,
      email: params.clientEmail,
      phone: params.clientPhone,
      timezone: params.timezone,
      preferredName: params.preferredName,
      gender: params.gender,
      pronoun: params.pronoun,
      birthday: params.birthday,
      unit: params.unit,
      street: params.street,
      city: params.city,
      province: params.province,
      country: params.country,
      postalCode: params.postalCode,
      emergencyName: params.emergencyName,
      emergencyPhone: params.emergencyPhone,
      referrer: params.referrer,
      referredBy: params.referredBy,
      referralCode: params.referralCode,
      partnershipId: params.partnershipId,
      marketingOptIn: params.marketingOptIn,
      smsOptIn: params.smsOptIn,
      newsletterOptIn: params.newsletterOptIn
    };
    
    await userService.updateProfile(params.userId, userProfileData, client);
    
    // 2. Ensure health record exists and is active
    const healthRecordId = await healthRecordService.ensure(params.userId, client);
    
    // 3. Check if an episode already exists for this therapy type
    // Ensure therapy type is uppercase for enum
    const therapyTypeForValidation = (effectiveTherapyType ?
      effectiveTherapyType.toUpperCase() : 'INDIVIDUAL') as TherapyType;

    console.log('[createInitialSession] Therapy type for validation:', {
      effectiveTherapyType,
      therapyTypeForValidation,
      isCouple: therapyTypeForValidation === 'COUPLES',
      isGroup: therapyTypeForValidation === 'GROUP'
    });
    
    const episodeValidation = await episodeService.validateEpisodeCreation(
      healthRecordId,
      therapyTypeForValidation,
      params.therapistId,
      client
    );
    
    // Check if we can proceed
    if (!episodeValidation.canCreate && !episodeValidation.existingEpisodeId) {
      // Cannot proceed - different therapist
      throw new Error(episodeValidation.message || 'Cannot create session');
    }
    
    let episodeId: string;
    let groupId: string | undefined; // Declare groupId at a higher scope
    let sessionId: string; // Declare sessionId at higher scope
    let createdGroupId: string | undefined; // Track what we created for cleanup
    let createdEpisodeId: string | undefined; // Track what we created for cleanup

    try {
      if (episodeValidation.existingEpisodeId && therapyTypeForValidation === 'INDIVIDUAL') {
        // Use existing episode with same therapist
        episodeId = episodeValidation.existingEpisodeId;
        console.log('Using existing episode:', episodeId);
      } else {
        // For couples/group therapy, create a therapy group first
        if (therapyTypeForValidation === 'COUPLES') {
          console.log('[createInitialSession] Creating therapy group for', therapyTypeForValidation);

          // Create a descriptive name with the client's name
          const clientName = params.preferredName || params.clientFirstName;
          const groupName = therapyTypeForValidation === 'COUPLES'
            ? `Couples Therapy: ${clientName}`
            : `Group Therapy: ${clientName}`;

          // Determine free sessions from promo code
          let freeSessionsAllocated = 0;
          if (effectivePromoCode) {
            const promoDetails = await validatePromoCode(effectivePromoCode, effectiveTherapistTier, client);
            if (promoDetails) {
              // Just use discountValue directly - all our promos are free sessions
              freeSessionsAllocated = promoDetails.discountValue || 0;
            }
          }

          const newGroupId = uuidv4();
          const groupData = {
            id: newGroupId,
            name: groupName,
            groupType: therapyTypeForValidation,
            leadTherapistId: params.therapistId,
            status: 'ACTIVE',
            primaryPayerUserId: params.userId,
            timezone: params.timezone,
            // Add the new billing and promo fields
            sessionRate: effectiveRate,
            tier: effectiveTherapistTier,
            promoCode: params.partnershipId ? undefined : effectivePromoCode, // No promo if partnership
            freeSessionsAllocated: params.partnershipId ? 0 : freeSessionsAllocated, // No free sessions if partnership
            partnershipId: params.partnershipId,
            partnershipSessions: params.partnershipSessions,
            partnershipAccessCode: params.accessCode, // Store the access code used
            isRateException: (params as any).isRateException || false,
            billingType: 'SINGLE_PAYER', // Default for couples therapy
            createdAt: new Date().toISOString(),  // Convert to ISO string for Supabase
            updatedAt: new Date().toISOString()   // Convert to ISO string for Supabase
          };

          console.log('[createInitialSession] Attempting to create therapy group:', groupData);

          const { data: newGroup, error: groupError } = await client
            .from('therapy_groups')
            .insert(groupData)
            .select()
            .single();

          if (groupError || !newGroup) {
            console.error('[createInitialSession] Error creating therapy group:', {
              error: groupError,
              groupData,
              errorMessage: groupError?.message,
              errorDetails: groupError?.details,
              errorHint: groupError?.hint,
              errorCode: groupError?.code
            });

            // Provide more specific error messages based on error details
            let errorMessage = 'Failed to create therapy group';
            if (groupError?.message?.includes('tier')) {
              errorMessage = 'Invalid therapist tier configuration. Please contact support.';
            } else if (groupError?.message?.includes('foreign key')) {
              errorMessage = 'Invalid therapist or user reference. Please try again.';
            } else if (groupError?.message) {
              errorMessage = `Failed to create therapy group: ${groupError.message}`;
            }

            throw new Error(errorMessage);
          }

          groupId = newGroup.id;
          createdGroupId = groupId; // Track for cleanup
          console.log('[createInitialSession] Created therapy group:', groupId);
        }

        // For couples/group therapy, don't duplicate promo info on episode (it's on the group)
        const episodeData: any = {
          healthRecordId,
          userId: params.userId,
          therapyType: therapyTypeForValidation, // Use uppercase version
          therapistId: params.therapistId,
          matchingQuizId: params.matchingQuizId,
          sessionRate: effectiveRate,
          tier: effectiveTherapistTier,
          timezone: params.timezone,
          partnershipId: params.partnershipId,
          partnershipSessions: params.partnershipSessions,
          accessCode: params.accessCode,
          groupId, // Add groupId to episode if it's couples/group therapy
          isRateException: (params as any).isRateException || false
        };

        // Only add promo code and free sessions to individual therapy episodes (not couples/group)
        if (therapyTypeForValidation === 'INDIVIDUAL' && !params.partnershipId) {
          episodeData.promoCode = effectivePromoCode;

          // Determine free sessions for individual therapy
          if (effectivePromoCode) {
            const promoDetails = await validatePromoCode(effectivePromoCode, effectiveTherapistTier, client);
            if (promoDetails) {
              episodeData.freeSessionsAllocated = promoDetails.discountValue || 0;
            }
          }
        }

        episodeId = await episodeService.create(episodeData, client);
        createdEpisodeId = episodeId; // Track for cleanup
      }

      // 4. Create the therapy session with secure values
      // We already have groupId from when we created the therapy group above
      sessionId = await createTherapySession(
        episodeId,
        healthRecordId,
        {
          ...params,
          promoCode: effectivePromoCode,
          sessionRate: effectiveRate,
          therapyType: therapyTypeForValidation, // Use uppercase version
          tier: effectiveTherapistTier,
          groupId // Pass groupId to session creation (already set if couples/group)
        },
        client
      );

      // If we got here, everything succeeded
      console.log('[createInitialSession] Successfully created all records:', {
        sessionId,
        episodeId,
        groupId
      });

    } catch (innerError) {
      // Cleanup any created records if something failed
      console.error('[createInitialSession] Error during creation, attempting cleanup:', innerError);

      // Try to cleanup created records (best effort, don't throw if cleanup fails)
      if (createdGroupId) {
        try {
          await client.from('therapy_groups').delete().eq('id', createdGroupId);
          console.log('[createInitialSession] Cleaned up therapy group:', createdGroupId);
        } catch (cleanupError) {
          console.error('[createInitialSession] Failed to cleanup therapy group:', cleanupError);
        }
      }

      if (createdEpisodeId) {
        try {
          await client.from('episodes').delete().eq('id', createdEpisodeId);
          console.log('[createInitialSession] Cleaned up episode:', createdEpisodeId);
        } catch (cleanupError) {
          console.error('[createInitialSession] Failed to cleanup episode:', cleanupError);
        }
      }

      throw innerError; // Re-throw the original error
    }
    
    // 5. Create audit log for compliance
    await client
      .from('audit_logs')
      .insert({
        id: uuidv4(),
        userId: params.userId,
        userRole: 'client',
        action: 'create',
        resource: 'session',
        resourceId: sessionId,
        details: {
          episodeId: episodeId,
          healthRecordId: healthRecordId,
          therapistId: params.therapistId,
          sessionStart: params.start,
          sessionEnd: params.end
        },
        createdAt: new Date(),
        updatedAt: new Date()
      });
    
    // 6. Save consent records for initial booking (only if not already consented)
    if (params.termsAccepted || params.privacyAccepted || params.consentAccepted || params.cancellationAccepted || params.crisisAccepted || params.fileSharingAccepted) {
      console.log('[Session] Checking and saving consent records for initial booking');
      
      const consents = [];
      
      // Check and save Terms of Service if accepted and not already consented
      if (params.termsAccepted) {
        const hasTermsConsent = await consentService.hasConsent(params.userId, 'Terms of Service', client);
        if (!hasTermsConsent) {
          consents.push({
            userId: params.userId,
            title: 'Terms of Service',
            isGiven: true,
            method: 'online',
            version: '2025-08-10',
            ipAddress: params.ipAddress
          });
        } else {
          console.log('[Session] User already has Terms of Service consent');
        }
      }
      
      // Check and save Privacy Policy if accepted and not already consented
      if (params.privacyAccepted) {
        const hasPrivacyConsent = await consentService.hasConsent(params.userId, 'Privacy Policy', client);
        if (!hasPrivacyConsent) {
          consents.push({
            userId: params.userId,
            title: 'Privacy Policy',
            isGiven: true,
            method: 'online',
            version: '2025-08-10',
            ipAddress: params.ipAddress
          });
        } else {
          console.log('[Session] User already has Privacy Policy consent');
        }
      }
      
      // Check and save Counselling Consent if accepted and not already consented
      if (params.consentAccepted) {
        const hasCounsellingConsent = await consentService.hasConsent(params.userId, 'Consent to Counselling and Psychotherapy Services', client);
        if (!hasCounsellingConsent) {
          consents.push({
            userId: params.userId,
            title: 'Consent to Counselling and Psychotherapy Services',
            isGiven: true,
            method: 'online',
            version: '2025-08-10',
            ipAddress: params.ipAddress
          });
        } else {
          console.log('[Session] User already has Counselling consent');
        }
      }
      
      // Check and save Cancellation Policy if accepted and not already consented
      if (params.cancellationAccepted) {
        const hasCancellationConsent = await consentService.hasConsent(params.userId, '24-Hour Cancellation Policy', client);
        if (!hasCancellationConsent) {
          consents.push({
            userId: params.userId,
            title: '24-Hour Cancellation Policy',
            isGiven: true,
            method: 'online',
            version: '2025-08-10',
            ipAddress: params.ipAddress
          });
        } else {
          console.log('[Session] User already has Cancellation Policy consent');
        }
      }
      
      // Check and save Crisis Understanding if accepted and not already consented
      if (params.crisisAccepted) {
        const hasCrisisConsent = await consentService.hasConsent(params.userId, 'Understanding - Not a Crisis Service', client);
        if (!hasCrisisConsent) {
          consents.push({
            userId: params.userId,
            title: 'Understanding - Not a Crisis Service',
            isGiven: true,
            method: 'online',
            version: '2025-08-10',
            ipAddress: params.ipAddress
          });
        } else {
          console.log('[Session] User already has Crisis Understanding consent');
        }
      }

      // Check and save File Sharing if accepted and not already consented
      if (params.fileSharingAccepted) {
        const hasFileSharingConsent = await consentService.hasConsent(params.userId, 'File Sharing', client);
        if (!hasFileSharingConsent) {
          consents.push({
            userId: params.userId,
            title: 'File Sharing',
            isGiven: true,
            method: 'online',
            version: '2025-08-10',
            ipAddress: params.ipAddress
          });
        } else {
          console.log('[Session] User already has File Sharing consent');
        }
      }

      if (consents.length > 0) {
        const savedConsents = await consentService.createMultiple(consents, client);
        if (!savedConsents) {
          console.error('[Session] Failed to save new consent records, but continuing with session creation');
        } else {
          console.log('[Session] Successfully saved', consents.length, 'new consent records');
        }
      } else {
        console.log('[Session] No new consent records needed - user already has all required consents');
      }
    }
    
    // 7. Prepare email data (to be sent by server-side function)
    let emailData = null;
    try {
      // Fetch minimal therapist data needed for email
      const { data: therapistData, error: therapistError } = await client
        .from('pros')
        .select('firstName, lastName, email, googleMeetLink, timezone')
        .eq('id', params.therapistId)
        .single();

      if (!therapistError && therapistData) {
        // Check if session is partnership covered and get partnership name
        let isPartnershipCovered = false;
        let partnershipName = null;

        if (params.partnershipId) {
          isPartnershipCovered = true;

          // Fetch partnership name for email
          const { data: partnership } = await client
            .from('partnerships')
            .select('name')
            .eq('id', params.partnershipId)
            .single();

          if (partnership) {
            partnershipName = partnership.name;
          }
        }

        emailData = {
          // Client info
          userId: params.userId,
          clientEmail: params.clientEmail,
          clientFirstName: params.clientFirstName,
          clientLastName: params.clientLastName,
          clientPreferredName: params.preferredName,

          // Session details
          sessionId,
          episodeId,
          sessionStart: new Date(params.start).toISOString(), // Convert to ISO string for serialization
          sessionDuration: 50, // 50 minute sessions
          timezone: params.timezone,
          therapyType: params.therapyType || 'INDIVIDUAL',
          sessionType: 'INTAKE', // Initial session is always an intake

          // Therapist info
          therapistId: params.therapistId,
          therapistFirstName: therapistData.firstName,
          therapistLastName: therapistData.lastName,
          therapistEmail: therapistData.email,
          therapistGoogleMeetLink: therapistData.googleMeetLink,
          therapistTimeZone: therapistData.timezone || 'America/Toronto',

          // Payment info
          sessionRate: params.sessionRate || 0,
          isPartnershipCovered,
          partnershipName
        };
        
        console.log('[Session] Email data prepared for session:', sessionId, {
          isPartnershipCovered,
          partnershipName,
          sessionRate: params.sessionRate
        });
      } else if (therapistError) {
        console.error('[Session] Error fetching therapist for email:', therapistError);
      }
    } catch (error) {
      console.error('[Session] Error preparing email data:', error);
    }
    
    // We already have groupId from when we created the therapy group
    // No need to fetch it again
    return {
      sessionId,
      episodeId,
      healthRecordId,
      userId: params.userId,
      groupId, // This was already set when we created the group (undefined for individual therapy)
      emailData
    };
    
  } catch (error) {
    console.error('[createInitialSession] Error creating initial session:', {
      error,
      params: {
        userId: params.userId,
        therapistId: params.therapistId,
        therapyType: params.therapyType,
        matchingQuizId: params.matchingQuizId
      }
    });

    // Provide more specific error messages
    if (error instanceof Error) {
      if (error.message.includes('therapy group')) {
        throw new Error(`Failed to set up couples/group therapy session: ${error.message}`);
      }
      if (error.message.includes('episode')) {
        throw new Error(`Failed to create therapy episode: ${error.message}`);
      }
    }

    throw error;
  }
}

/**
 * Get all sessions for an episode
 */
export async function getSessionsByEpisode(
  episodeId: string,
  client?: SupabaseClient
): Promise<Session[] | null> {
  const supabase = (client || supabaseClient) as any;

  try {
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .eq('episodeId', episodeId)
      .order('start', { ascending: false });

    if (error) {
      console.error('Error fetching sessions for episode:', error);
      return null;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getSessionsByEpisode:', error);
    return null;
  }
}

/**
 * Get all sessions for a therapy group
 */
export async function getSessionsByGroup(
  groupId: string,
  client?: SupabaseClient
): Promise<Session[] | null> {
  const supabase = (client || supabaseClient) as any;

  try {
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .eq('groupId', groupId)
      .order('start', { ascending: false });

    if (error) {
      console.error('Error fetching sessions for group:', error);
      return null;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getSessionsByGroup:', error);
    return null;
  }
}

/**
 * Check if a session should be partnership covered based on remaining sessions
 */
async function checkPartnershipCoverage(
  episode: any | null,
  group: any | null,
  supabase: SupabaseClient
): Promise<{ isCovered: boolean; remaining: number; partnershipId?: string }> {
  let partnershipId: string | undefined;
  let partnershipSessions: number | undefined;
  let queryField: string;
  let queryValue: string | undefined;

  // Get partnership data from either episode or group
  if (group) {
    partnershipId = group.partnershipId;
    partnershipSessions = group.partnershipSessions;
    queryField = 'groupId';
    queryValue = group.id;
  } else if (episode) {
    partnershipId = episode.partnershipId;
    partnershipSessions = episode.partnershipSessions;
    queryField = 'episodeId';
    queryValue = episode.id;
  } else {
    return { isCovered: false, remaining: 0 };
  }

  // If no partnership, return not covered
  if (!partnershipId || !partnershipSessions) {
    return { isCovered: false, remaining: 0 };
  }

  // Count sessions that used partnership coverage
  // Include: SCHEDULED, CONFIRMED, COMPLETED, or paid late cancels/no-shows
  const { data: usedSessions } = await supabase
    .from('sessions')
    .select('id, status, paymentStatus')
    .eq(queryField, queryValue)
    .eq('isPartnershipCovered', true)
    .or(
      'status.in.(SCHEDULED,CONFIRMED,COMPLETED),' +
      'and(status.eq.NO_SHOW,paymentStatus.eq.PAID),' +
      'and(status.eq.LATE_CANCEL,paymentStatus.eq.PAID)'
    );

  const partnershipSessionsUsed = usedSessions?.length || 0;
  const remaining = Math.max(0, partnershipSessions - partnershipSessionsUsed);
  const isCovered = remaining > 0;

  console.log('[checkPartnershipCoverage] Coverage check:', {
    type: group ? 'group' : 'episode',
    id: queryValue,
    partnershipId,
    totalSessions: partnershipSessions,
    used: partnershipSessionsUsed,
    remaining,
    isCovered,
    sessions: usedSessions?.map(s => ({ id: s.id, status: s.status, payment: s.paymentStatus }))
  });

  return { isCovered, remaining, partnershipId };
}

/**
 * Booking validation result
 */
export interface BookingValidation {
  canBook: boolean;
  reason?: string;
  freeSessions?: number;
  unpaidSessions?: number;
  hasIntake?: boolean;
  unpaidSessionDetails?: Array<{
    id: string;
    date: string;
    status: string;
    paymentStatus: string;
  }>;
}

/**
 * Validate if a client can book a new session based on business rules
 */
export async function validateBooking(
  episodeId: string,
  sessionType: 'INTAKE' | 'FOLLOWUP' | 'CONSULTATION',
  client?: SupabaseClient
): Promise<BookingValidation> {
  const supabase = (client || supabaseClient) as any;
  
  try {
    // Get episode with sessions
    const { data: episode, error: episodeError } = await supabase
      .from('episodes')
      .select(`
        *,
        sessions:sessions (
          id,
          status,
          paymentStatus,
          sessionType,
          start,
          createdAt
        )
      `)
      .eq('id', episodeId)
      .single();

    if (episodeError || !episode) {
      return { 
        canBook: false, 
        reason: 'Episode not found' 
      };
    }

    const sessions = episode.sessions || [];

    // Check for existing intake session (excluding cancelled, no-shows, late cancellations, and rescheduled)
    if (sessionType === 'INTAKE') {
      const hasIntake = sessions.some((s: any) => 
        s.sessionType === 'INTAKE' && 
        s.status !== 'CANCELLED' &&
        s.status !== 'NO_SHOW' &&
        s.status !== 'LATE_CANCEL' &&
        s.status !== 'RESCHEDULED'
      );

      if (hasIntake) {
        return { 
          canBook: false, 
          reason: 'Client already has an intake session.',
          hasIntake: true
        };
      }
    }

    // Check for unpaid billable sessions (COMPLETED, LATE_CANCEL, NO_SHOW)
    const unpaidSessions = sessions.filter((s: any) => {
      // Only check billable session statuses
      const isBillableStatus = s.status === 'COMPLETED' || 
                               s.status === 'LATE_CANCEL' || 
                               s.status === 'NO_SHOW';
      
      if (!isBillableStatus) {
        return false;
      }
      
      // Debug: Log billable sessions to understand the issue
      console.log('[validateBooking] Checking billable session:', {
        sessionId: s.id,
        status: s.status,
        paymentStatus: s.paymentStatus,
        paymentStatusType: typeof s.paymentStatus,
        start: s.start,
        createdAt: s.createdAt
      });
      
      // Check if payment is unpaid or pending
      const isUnpaid = !s.paymentStatus || 
                       s.paymentStatus === 'UNPAID' || 
                       s.paymentStatus === 'PENDING';
      
      if (isUnpaid) {
        console.log('[validateBooking] Found unpaid billable session:', {
          sessionId: s.id,
          status: s.status,
          paymentStatus: s.paymentStatus,
          reason: !s.paymentStatus ? 'No payment status' : 
                  s.paymentStatus === 'UNPAID' ? 'Status is UNPAID' :
                  s.paymentStatus === 'PENDING' ? 'Status is PENDING' : 'Unknown'
        });
      }
      
      return isUnpaid;
    });

    if (unpaidSessions.length > 0) {
      // Format the dates of unpaid sessions in PST
      const unpaidSessionDates = unpaidSessions.map((session: any) => {
        // Ensure session start has 'Z' suffix for UTC
        const startDateStr = session.start.endsWith('Z') ? session.start : `${session.start}Z`;
        const sessionDate = new Date(startDateStr);

        // Format in PST timezone - just the date
        const formattedDate = sessionDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'America/Los_Angeles'
        });
        return formattedDate;
      });

      // Create detailed message with all unpaid session dates
      let reasonMessage = unpaidSessions.length === 1
        ? `Client has an unpaid session from ${unpaidSessionDates[0]}. Payment required before booking new sessions.`
        : `Client has ${unpaidSessions.length} unpaid sessions:\n${unpaidSessionDates.map((date: string) => `• ${date}`).join('\n')}\n\nPayment required before booking new sessions.`;

      return {
        canBook: false,
        reason: reasonMessage,
        unpaidSessions: unpaidSessions.length,
        unpaidSessionDetails: unpaidSessions.map((s: any) => ({
          id: s.id,
          date: s.start.endsWith('Z') ? s.start : `${s.start}Z`, // Ensure 'Z' suffix in returned data
          status: s.status,
          paymentStatus: s.paymentStatus
        }))
      };
    }

    // Check for free sessions by counting actual usage
    const freeSessionsUsed = await episodeService.getFreeSessionsUsed(episodeId, supabase);
    const freeSessionsAllocated = episode.freeSessionsAllocated || 0;
    const remainingFreeSessions = Math.max(0, freeSessionsAllocated - freeSessionsUsed);

    return { 
      canBook: true,
      freeSessions: remainingFreeSessions
    };
  } catch (error) {
    console.error('[Session Service] Validation error:', error);
    return { 
      canBook: false, 
      reason: 'Failed to validate booking eligibility' 
    };
  }
}

/**
 * Create a therapy session booking with all business rules applied
 */
export interface CreateBookingParams {
  episodeId?: string; // For individual therapy
  groupId?: string; // For couples/group therapy
  expectedAttendees?: string[]; // User IDs expected to attend (for couples/group)
  sessionType: 'INTAKE' | 'FOLLOWUP' | 'CONSULTATION';
  start: string; // UTC timestamp
  end: string; // UTC timestamp
  duration: number; // in minutes
  isReschedule?: boolean; // Skip validation for reschedules
  bookedBy?: { // Who performed the booking action
    userId?: string; // Client user ID if booked by client
    proId?: string; // Therapist ID if booked by therapist
    role: 'client' | 'therapist' | 'system'; // Actor role
  };
}

export interface BookingResult {
  success: boolean;
  sessionId?: string;
  error?: string;
  usedFreeSession?: boolean;
  isConsultation?: boolean;
  emailData?: {
    therapist: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      timezone?: string;
      googleMeetLink?: string;
    };
    client: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      preferredName?: string;
      phone?: string;
      attendees?: any[]; // For group therapy sessions
    };
    session: {
      id: string;
      start: string;
      end: string;
      type: string;
      therapyType: string;
      timezone: string;
      groupId?: string | null; // For group therapy sessions
      expectedAttendees?: string[]; // User IDs for group therapy
    };
    episode: {
      id: string;
      sessionRate: number;
      promoCode?: string;
      isFreeSession: boolean;
      employerMemberId?: string | null;
      employerOrgId?: string | null;
    };
    isPartnershipCovered?: boolean;
    partnershipName?: string | null;
  };
}

export async function createBooking(
  params: CreateBookingParams,
  client?: SupabaseClient
): Promise<BookingResult> {
  const supabase = (client || supabaseClient) as any;

  try {
    // Validate that either episodeId or groupId is provided
    if (!params.episodeId && !params.groupId) {
      return {
        success: false,
        error: 'Either episodeId or groupId is required'
      };
    }

    let episode: any;
    let episodeId: string;
    let therapyGroup: any = null;

    // Handle group therapy bookings
    if (params.groupId) {

      // Get group details with episodes and therapist info
      const { data: group, error: groupError } = await supabase
        .from('therapy_groups')
        .select(`
          *,
          episodes (
            id,
            userId,
            sessionRate,
            freeSessionsAllocated,
            therapistId
          )
        `)
        .eq('id', params.groupId)
        .single();

      if (groupError || !group) {
        return {
          success: false,
          error: 'Therapy group not found'
        };
      }

      therapyGroup = group; // Store the group data for later use

      // Find the primary payer's episode for rate information
      const primaryPayerEpisode = group.episodes?.find((ep: any) =>
        ep.userId === group.primaryPayerUserId
      );

      if (!primaryPayerEpisode) {
        // Fallback to first episode if no primary payer found
        console.warn('[Session Service] No primary payer episode found, using first episode');
        const firstEpisode = group.episodes?.[0];
        if (!firstEpisode) {
          return {
            success: false,
            error: 'No episodes found for therapy group'
          };
        }
        episodeId = firstEpisode.id;
      } else {
        episodeId = primaryPayerEpisode.id;
      }

      // Fetch the full episode details
      const { data: episodeData, error: episodeError } = await supabase
        .from('episodes')
        .select(`
          *,
          therapist:pros!episodes_therapistId_fkey (
            id,
            firstName,
            lastName,
            email,
            timezone,
            googleMeetLink
          )
        `)
        .eq('id', episodeId)
        .single();

      if (episodeError || !episodeData) {
        return {
          success: false,
          error: 'Episode not found'
        };
      }

      episode = episodeData;
      episode.therapist = episode.therapist;

    } else {
      // Individual therapy booking
      episodeId = params.episodeId!;

      // Skip validation for reschedules to avoid race conditions
      if (!params.isReschedule) {
        // Validate booking eligibility
        const validation = await validateBooking(episodeId, params.sessionType, supabase);

        if (!validation.canBook) {
          return {
            success: false,
            error: validation.reason
          };
        }
      }

      // Get episode details with therapist info
      const { data: episodeData, error: episodeError } = await supabase
        .from('episodes')
        .select(`
          *,
          therapist:pros!episodes_therapistId_fkey (
            id,
            firstName,
            lastName,
            email,
            timezone,
            googleMeetLink
          ),
          user:users!episodes_userId_fkey (
            id,
            firstName,
            lastName,
            preferredName,
            email,
            phone
          )
        `)
        .eq('id', episodeId)
        .single();

      if (episodeError || !episodeData) {
        return {
          success: false,
          error: 'Episode not found'
        };
      }

      episode = episodeData;
    }

    // Check for scheduling conflicts. Only ACTIVE statuses occupy the slot —
    // CANCELLED / LATE_CANCEL / NO_SHOW / RESCHEDULED all freed the time, so
    // the therapist can book a replacement client into that slot. (The
    // original cancellation row is kept on file for billing / records, it
    // just doesn't reserve the calendar anymore.)
    const { data: conflicts, error: conflictError } = await supabase
      .from('sessions')
      .select('id')
      .eq('therapistId', episode.therapistId)
      .in('status', ['SCHEDULED', 'CONFIRMED', 'COMPLETED'])
      .lt('start', params.end)
      .gt('end', params.start);

    if (conflictError) {
      console.error('[Session Service] Error checking conflicts:', conflictError);
      return {
        success: false,
        error: 'Failed to check scheduling conflicts'
      };
    }

    if (conflicts && conflicts.length > 0) {
      return {
        success: false,
        error: 'Time slot conflicts with another session'
      };
    }

    // Check for partnership coverage first
    const partnershipCoverage = await checkPartnershipCoverage(
      params.groupId ? null : episode,
      params.groupId ? therapyGroup : null,
      supabase
    );

    // Determine if using a free session by checking allocation vs used
    let freeSessionsRemaining = 0;
    let useFreeSession = false;

    // Only check free sessions if not partnership covered
    if (!partnershipCoverage.isCovered) {
      if (params.groupId && therapyGroup) {
        // For group therapy, check the therapy group's free sessions
        const freeSessionsUsed = await episodeService.getFreeSessionsUsed(params.groupId, supabase, 'group');
        freeSessionsRemaining = Math.max(0, (therapyGroup.freeSessionsAllocated || 0) - freeSessionsUsed);
        useFreeSession = freeSessionsRemaining > 0;
      } else {
        // For individual therapy, check the episode's free sessions
        const freeSessionsUsed = await episodeService.getFreeSessionsUsed(episodeId, supabase, 'episode');
        freeSessionsRemaining = Math.max(0, (episode.freeSessionsAllocated || 0) - freeSessionsUsed);
        useFreeSession = freeSessionsRemaining > 0;
      }
    }

    // Check if this is a consultation session (always free)
    const isConsultation = params.sessionType === 'CONSULTATION';

    // Calculate the prorated rate based on duration
    // Base rate is for 50 minutes, so for 75 minutes multiply by 1.5
    let sessionRate = episode.sessionRate || 0;
    if (!isConsultation && params.duration === 75) {
      // Prorate the rate: 75 minutes = 1.5x the 50-minute rate
      sessionRate = Math.round(sessionRate * 1.5);
      console.log('[Session Service] Prorating rate for 75-minute session:', {
        baseRate: episode.sessionRate,
        proratedRate: sessionRate,
        duration: params.duration
      });
    }

    // Check for available wallet (EMPLOYER_MHAP, SCHOOL, PROMO, etc.)
    // Only check if not already covered by consultation, partnership, or free
    // sessions. We also gate on `episode.sessionRate === 0` — a wallet must
    // only fund episodes that were explicitly set up as covered (imwell school
    // signup, employer MHAP intake). A self-pay episode (rate > 0) must never
    // be silently diverted to a wallet the client happens to have acquired
    // through an unrelated org enrollment, because it would flip the
    // therapist's expected billing (e.g. $45 → $0) mid-engagement.
    let walletResult: { wallet: any; balance: any } | null = null;
    let useWallet = false;

    if (!isConsultation && !partnershipCoverage.isCovered && !useFreeSession && sessionRate === 0) {
      const candidate = await findBestAvailableWallet(episode.userId, supabase);
      // If the episode is linked to a specific org, require the wallet's
      // sourceId to match. A client can hold multiple org-scoped wallets
      // simultaneously (dependent on employer plan A + school enrollment at
      // org B); each session must draw from the plan the episode was booked
      // under, not whichever wallet happens to sort first.
      const orgMatches =
        !episode.employerOrgId || candidate?.wallet.sourceId === episode.employerOrgId;
      if (candidate && candidate.balance.available > 0 && orgMatches) {
        walletResult = candidate;
        useWallet = true;
        console.log('[Session Service] Found wallet for client:', {
          userId: episode.userId,
          walletId: candidate.wallet.id,
          sourceType: candidate.wallet.sourceType,
          available: candidate.balance.available
        });
      } else if (candidate) {
        console.log('[Session Service] Skipping wallet — org mismatch with episode:', {
          userId: episode.userId,
          walletId: candidate.wallet.id,
          walletSourceId: candidate.wallet.sourceId,
          episodeEmployerOrgId: episode.employerOrgId
        });
      }
    }

    // Create the session
    const sessionId = uuidv4();
    const now = new Date().toISOString();

    // For group therapy, automatically populate expectedAttendees from the group if not provided
    let expectedAttendees: string[] = [];
    if (params.groupId && therapyGroup) {
      if (params.expectedAttendees && params.expectedAttendees.length > 0) {
        // Use the provided expectedAttendees if specified
        expectedAttendees = params.expectedAttendees;
      } else {
        // Otherwise, assume all group members are attending
        expectedAttendees = therapyGroup.episodes?.map((ep: any) => ep.userId).filter(Boolean) || [];
        console.log('[Session Service] Auto-populated expectedAttendees from group (all members):', expectedAttendees);
      }
    } else if (params.expectedAttendees) {
      // For individual therapy, use provided expectedAttendees if any
      expectedAttendees = params.expectedAttendees;
    }

    const sessionData: any = {
      id: sessionId,
      episodeId: params.groupId ? null : episodeId, // null for group therapy, episodeId for individual
      groupId: params.groupId || null, // Include groupId for couples/group therapy
      therapistId: episode.therapistId,
      sessionType: params.sessionType,
      start: params.start,
      end: params.end,
      timezone: episode.timezone || 'America/New_York', // Use episode timezone, default to EST
      duration: params.duration,
      status: 'SCHEDULED' as const,
      // Determine payment status based on coverage (consultation, free session, wallet, or pending)
      paymentStatus: (isConsultation || useFreeSession || useWallet) ? 'WAIVED' as const : 'PENDING' as const,
      // Determine rate: consultations, free sessions, and wallet-covered are $0
      rate: (isConsultation || useFreeSession || useWallet) ? 0 : sessionRate,
      expectedAttendees: expectedAttendees, // Include expected attendees for couples/group therapy
      actualAttendees: [],   // Empty initially
      isPartnershipCovered: partnershipCoverage.isCovered,
      partnershipId: partnershipCoverage.partnershipId || null,
      // MHAP/Employer tracking - snapshot from episode for historical reporting
      employerOrgId: episode.employerOrgId || null,
      memberId: episode.employerMemberId || null,
      createdAt: now,
      updatedAt: now
    };

    // Add wallet fields if using wallet
    if (useWallet && walletResult) {
      sessionData.walletId = walletResult.wallet.id;
      // Map WalletSource → PaymentSource (SCHOOL / PURCHASED / GIFT / etc.
      // are not valid PaymentSource enum values; inserting the raw wallet
      // sourceType fails on the sessions row's paymentSource enum check).
      sessionData.paymentSource = mapWalletSourceToPaymentSource(walletResult.wallet.sourceType);

      // Backfill employerOrgId from the wallet when the episode doesn't have
      // one recorded — applies to both EMPLOYER_MHAP and SCHOOL wallets, since
      // both carry orgId in sourceId and both map to EMPLOYER_MHAP on the
      // session row.
      const isOrgScopedWallet =
        walletResult.wallet.sourceType === 'EMPLOYER_MHAP' ||
        walletResult.wallet.sourceType === 'SCHOOL';
      if (isOrgScopedWallet && !sessionData.employerOrgId) {
        sessionData.employerOrgId = walletResult.wallet.sourceId || null;
      }
    }

    const { error: insertError } = await supabase
      .from('sessions')
      .insert(sessionData);

    if (insertError) {
      console.error('[Session Service] Error creating session:', insertError);
      return {
        success: false,
        error: 'Failed to create session'
      };
    }

    // Reserve session from wallet if using wallet
    if (useWallet && walletResult) {
      const reserved = await reserveWalletSession(
        walletResult.wallet.id,
        sessionId,
        episode.userId,
        supabase
      );

      if (!reserved) {
        // Rollback session creation if wallet reservation fails
        console.error('[Session Service] Failed to reserve wallet session, rolling back');
        await supabase.from('sessions').delete().eq('id', sessionId);
        return {
          success: false,
          error: 'Failed to reserve session from wallet'
        };
      }
    }

    // Log session creation details
    if (isConsultation) {
      console.log('[Session Service] Consultation session booked (free)');
    } else if (partnershipCoverage.isCovered) {
      console.log('[Session Service] Partnership covered session booked. Remaining partnership sessions:', partnershipCoverage.remaining - 1);
    } else if (useFreeSession) {
      console.log('[Session Service] Free session used. Remaining free sessions:', freeSessionsRemaining - 1);
    } else if (useWallet && walletResult) {
      console.log('[Session Service] Wallet session booked:', {
        walletId: walletResult.wallet.id,
        sourceType: walletResult.wallet.sourceType,
        remaining: walletResult.balance.available - 1
      });
    } else {
      console.log('[Session Service] Regular paid session booked. Rate:', sessionRate);
    }

    // Log the booking with correct actor attribution
    // Default to therapist role if not specified (for backwards compatibility)
    const bookedByRole = params.bookedBy?.role || 'therapist';

    // Build audit log entry based on who booked
    const auditLogEntry: any = {
      id: uuidv4(),
      action: 'session.booked',
      resource: 'sessions',
      resourceId: sessionId,
      details: {
        episodeId: params.groupId ? null : episodeId,
        groupId: params.groupId || null,
        usedFreeSession: useFreeSession,
        usedWallet: useWallet,
        walletId: useWallet && walletResult ? walletResult.wallet.id : null,
        walletSourceType: useWallet && walletResult ? walletResult.wallet.sourceType : null,
        isPartnershipCovered: partnershipCoverage.isCovered,
        partnershipId: partnershipCoverage.partnershipId,
        sessionType: params.sessionType,
        start: params.start,
        end: params.end,
        expectedAttendees: expectedAttendees,
        therapistId: episode.therapistId,
        clientId: episode.userId,
        bookedBy: bookedByRole // Track who actually performed the booking
      }
    };

    // Set proId or userId based on who booked the session
    if (bookedByRole === 'client') {
      auditLogEntry.userId = params.bookedBy?.userId || episode.userId;
    } else {
      // therapist or system
      auditLogEntry.proId = params.bookedBy?.proId || episode.therapistId;
    }

    await supabase
      .from('audit_logs')
      .insert(auditLogEntry);

    // Prepare client data for email - handle both individual and group therapy
    let clientData;
    if (params.groupId && expectedAttendees && expectedAttendees.length > 0) {
      // For group therapy, fetch attendee information
      const { data: attendees } = await supabase
        .from('users')
        .select('id, firstName, lastName, preferredName, email, phone')
        .in('id', expectedAttendees);

      // Fetch each attendee's episode to get their MHAP status
      const { data: attendeeEpisodes } = await supabase
        .from('episodes')
        .select('userId, employerMemberId, employerOrgId')
        .eq('groupId', params.groupId);

      // Enrich attendees with their MHAP status
      const enrichedAttendees = (attendees || []).map((attendee: { id: string; firstName: string; lastName: string; preferredName?: string | null; email: string; phone?: string | null }) => {
        const attendeeEpisode = attendeeEpisodes?.find((ep: { userId: string; employerMemberId?: string | null; employerOrgId?: string | null }) => ep.userId === attendee.id);
        return {
          ...attendee,
          employerMemberId: attendeeEpisode?.employerMemberId || null,
          employerOrgId: attendeeEpisode?.employerOrgId || null
        };
      });

      // Use first attendee as primary contact (or could use primary payer)
      const primaryAttendee = enrichedAttendees[0];
      clientData = {
        id: primaryAttendee?.id || '',
        email: primaryAttendee?.email || '',
        firstName: primaryAttendee?.firstName || '',
        lastName: primaryAttendee?.lastName || '',
        preferredName: primaryAttendee?.preferredName,
        phone: primaryAttendee?.phone,
        // Include all attendees for group therapy emails (with MHAP status)
        attendees: enrichedAttendees
      };
    } else {
      // Individual therapy - use episode user data
      // If the user join didn't return a name, fetch it directly
      let userData = episode.user;
      if (!userData?.firstName && episode.userId) {
        const { data: directUser } = await supabase
          .from('users')
          .select('id, firstName, lastName, preferredName, email, phone')
          .eq('id', episode.userId)
          .single();
        if (directUser) {
          userData = directUser;
        }
      }
      clientData = {
        id: episode.userId,
        email: userData?.email || '',
        firstName: userData?.firstName || '',
        lastName: userData?.lastName || '',
        preferredName: userData?.preferredName,
        phone: userData?.phone
      };
    }

    // Get partnership name if covered
    let partnershipName = null;
    if (partnershipCoverage.isCovered && partnershipCoverage.partnershipId) {
      const { data: partnership } = await supabase
        .from('partnerships')
        .select('name')
        .eq('id', partnershipCoverage.partnershipId)
        .single();

      if (partnership) {
        partnershipName = partnership.name;
      }
    }

    // Return booking result with additional data for email notifications
    // Email notifications should be sent from a server-side API route
    return {
      success: true,
      sessionId,
      usedFreeSession: isConsultation ? false : useFreeSession, // Don't count consultations as free sessions
      isConsultation,
      // Include data needed for email notifications
      emailData: {
        therapist: {
          id: episode.therapistId,
          email: episode.therapist?.email || '',
          firstName: episode.therapist?.firstName || '',
          lastName: episode.therapist?.lastName || '',
          timezone: episode.therapist?.timezone || episode.timezone,
          googleMeetLink: episode.therapist?.googleMeetLink
        },
        client: clientData,
        session: {
          id: sessionId,
          start: params.start.endsWith('Z') ? params.start : params.start + 'Z',
          end: params.end.endsWith('Z') ? params.end : params.end + 'Z',
          type: params.sessionType,
          therapyType: params.groupId ? (episode.therapyType || 'COUPLES') : 'INDIVIDUAL',
          timezone: episode.timezone || 'America/New_York',
          groupId: params.groupId || null,
          expectedAttendees: expectedAttendees
        },
        isPartnershipCovered: partnershipCoverage.isCovered,
        partnershipName: partnershipName,
        episode: {
          id: episodeId, // Use the primary payer's episodeId for billing reference
          sessionRate: sessionRate, // Use the prorated rate
          promoCode: episode.promoCode,
          isFreeSession: useFreeSession,
          // MHAP fields for correct dashboard URL in emails
          employerMemberId: episode.employerMemberId,
          employerOrgId: episode.employerOrgId
        }
      }
    };
  } catch (error) {
    console.error('[Session Service] Error creating booking:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to create session' 
    };
  }
}